import { ALL_SOCIAL_PLATFORMS, SOCIAL_PLATFORMS } from '~/constants';
import { ISearchContentResult } from '~/searchApi';
import * as viewModel from '~/types';
import { CreatorType, ManagerAgency } from '~/types';
import * as notificationsViewModel from '~/types/notification';

import * as clientApiViewModel from './clientApi.types';
import * as clientApiCreatorModel from './creator.model';
import * as clientApiNotificationModel from './notifications.model';

export const mapCreatorPlatformInstagramFromApiModelToViewModel = ({
  followersCount,
  graphAuthReminderSentAt,
  graphAuthReminderSentCount,
  handle,
  higher,
  isFirstGraphAuth,
  isGraphTokenValid,
  location,
  name,
  profilePicUrl,
  shouldGraphReAuth,
  status,
}: clientApiCreatorModel.CreatorPlatformInstagram): clientApiViewModel.ICreatorPlatformStatus => ({
  avatar: profilePicUrl,
  followersCount,
  handle,
  higher,
  isConnected: !isFirstGraphAuth,
  isTokenValid: isGraphTokenValid,
  location,
  name,
  reminderSentAt: graphAuthReminderSentAt,
  reminderSentCount: graphAuthReminderSentCount,
  shouldReAuth: shouldGraphReAuth,
  status,
});

export const mapCreatorPlatformTiktokFromApiModelToViewModel = ({
  followersCount,
  higher,
  isTiktokConnected,
  isTiktokTokenValid,
  location,
  name,
  profileImage,
  shouldTiktokReAuth,
  tiktokAuthReminderSentAt,
  tiktokAuthReminderSentCount,
  tiktokHandle,
  status,
}: clientApiCreatorModel.CreatorPlatformTiktok): clientApiViewModel.ICreatorPlatformStatus => ({
  avatar: profileImage,
  followersCount,
  handle: tiktokHandle,
  higher,
  isConnected: isTiktokConnected,
  isTokenValid: isTiktokTokenValid,
  location,
  name,
  reminderSentAt: tiktokAuthReminderSentAt,
  reminderSentCount: tiktokAuthReminderSentCount,
  shouldReAuth: shouldTiktokReAuth,
  status,
});

export const mapCreatorPlatformYoutubeFromApiModelToViewModel = ({
  higher,
  location,
  handle,
  profileImage,
  isYoutubeConnected,
  isYoutubeTokenValid,
  shouldYtReAuth,
  subscriberCount,
  ytAuthReminderSentAt,
  ytAuthReminderSentCount = 0,
  channelName,
  status,
}: clientApiCreatorModel.CreatorPlatformYoutube): clientApiViewModel.ICreatorPlatformStatus => ({
  avatar: profileImage,
  followersCount: subscriberCount,
  higher,
  location,
  handle,
  isConnected: isYoutubeConnected,
  isTokenValid: isYoutubeTokenValid,
  reminderSentAt: ytAuthReminderSentAt,
  reminderSentCount: ytAuthReminderSentCount,
  shouldReAuth: shouldYtReAuth,
  name: channelName,
  status,
});

export const mapCreatorPlatformSnapchatFromApiModelToViewModel = ({
  followersCount,
  snapUserName,
  displayName,
  profileUrl,
  isSnapchatConnected,
  isSnapchatTokenValid,
  snapchatAuthReminderSentAt,
  snapchatAuthReminderSentCount,
  shouldSnapchatReAuth,
  status,
}: clientApiCreatorModel.CreatorPlatformSnapchat): clientApiViewModel.ICreatorPlatformStatus => ({
  followersCount: followersCount,
  handle: snapUserName,
  avatar: profileUrl,
  isConnected: isSnapchatConnected,
  isTokenValid: isSnapchatTokenValid,
  name: displayName,
  reminderSentAt: snapchatAuthReminderSentAt,
  reminderSentCount: snapchatAuthReminderSentCount,
  shouldReAuth: shouldSnapchatReAuth,
  status,
});

export const mapCreatorPlatformsFromApiModelToViewModel = ({
  platforms,
}: clientApiCreatorModel.CreatorPlatforms): clientApiViewModel.CreatorPlatformsType => ({
  [SOCIAL_PLATFORMS.instagram]:
    platforms.instagram &&
    mapCreatorPlatformInstagramFromApiModelToViewModel(platforms.instagram),
  [SOCIAL_PLATFORMS.tiktok]:
    platforms.tiktok &&
    mapCreatorPlatformTiktokFromApiModelToViewModel(platforms.tiktok),
  [SOCIAL_PLATFORMS.youtube]:
    platforms.youtube &&
    mapCreatorPlatformYoutubeFromApiModelToViewModel(platforms.youtube),
  [ALL_SOCIAL_PLATFORMS.snapchat]:
    platforms.snapchat &&
    mapCreatorPlatformSnapchatFromApiModelToViewModel(platforms.snapchat),
});

export const mapCreatorAgencyContentFromApiModelToViewModel = (
  content: clientApiCreatorModel.CreatorAgencyContentSelected,
): ISearchContentResult => ({
  ...(content as any),
  contentId: content.id,
});

export const mapCreatorComanagersFromApiModelToViewModel = (
  coManagers: viewModel.CoManager | undefined,
): {
  id: string;
  name: string;
  email: string;
  agency: ManagerAgency;
  updatedAt: string;
}[] => {
  if (!coManagers) {
    return [];
  }

  return Object.values(coManagers).map((comanager) => {
    // added email as for the feat. [FOAM-6912] assign a Creator to a Manager with a pending invitation
    return {
      id: comanager.coManager.id,
      name: comanager.coManager.name,
      email: comanager.coManager.email,
      agency: comanager.coManager.agency,
      updatedAt: comanager.updatedAt,
    };
  });
};

export const mapCreatorFromApiModelToViewModel = ({
  age,
  agency,
  avatar,
  authId,
  belongsToSameAgency,
  benchmark,
  bioAgency,
  biography,
  categoryAgency,
  city,
  contentAgencySelected,
  dateOfBirth,
  duplicatedBy,
  duplicatedCreator,
  email,
  firstName,
  fullName,
  genderAgency,
  highlightedContent,
  id,
  insight,
  hasConnectedPlatform,
  isCoManager,
  isCoManagerRequestPending,
  isManagerOwner,
  isOnCampaign,
  isPubliclyVisible,
  lastName,
  location,
  manager,
  notes,
  platforms,
  slug,
  status,
  scp,
  snapchatInsight,
  tiktokInsight,
  totalAudience,
  ytChannels,
  ytTokens,
  echoWebStatus,
  echoWelcomeSeen,
  createdAt,
  profilePicUrl,
  name,
  coManagers,
  isOffPlatform,
  webLinks,
  platformFullAccess,
  inviteStatus,
  invitations,
  updatedAt,
}: clientApiCreatorModel.Creator): viewModel.ICreator => {
  return {
    age,
    agency,
    avatar,
    authId,
    belongsToSameAgency,
    benchmark,
    biography,
    bioAgency,
    categoryAgency,
    email,
    echoWebStatus,
    echoWelcomeSeen,
    dateOfBirth,
    duplicatedBy,
    duplicatedCreator,
    genderAgency,
    firstName,
    fullName,
    id,
    insight,
    lastName,
    location,
    city,
    manager,
    notes,
    platforms: mapCreatorPlatformsFromApiModelToViewModel({ platforms }),
    hasConnectedPlatform,
    isCoManager,
    isManagerOwner,
    isCoManagerRequestPending,
    isOnCampaign,
    isPubliclyVisible,
    slug,
    status,
    scp,
    snapchatInsight,
    tiktokInsight,
    totalAudience,
    ytChannels,
    ytTokens,
    highlightedContent: highlightedContent?.map(
      mapCreatorAgencyContentFromApiModelToViewModel,
    ),
    contentAgencySelected: contentAgencySelected?.map(
      mapCreatorAgencyContentFromApiModelToViewModel,
    ),
    createdAt,
    profilePicUrl,
    name,
    coManagers: mapCreatorComanagersFromApiModelToViewModel(coManagers),
    isOffPlatform,
    webLinks,
    platformFullAccess,
    inviteStatus,
    invitations,
    updatedAt,
  };
};

export const mapIcreatorToCreatorType = (
  creator: viewModel.ICreator,
  //@ts-ignore
): CreatorType => ({
  ...creator,
  age: creator.age,
  agency: {
    name: creator.manager?.name ?? '',
    profilePicUrl: creator.manager?.agencyLogo,
    brandDealsAutoSubmitEnabled: creator.agency?.brandDealsAutoSubmitEnabled,
  },
  email: creator.email,
  benchmark: creator.benchmark,
  firstName: creator.firstName,
  lastName: creator.lastName,
  slug: creator.slug,
  biography: creator.biography,
  coManagerObjectId: creator.coManagerObjectId,
  echoWebStatus: creator.echoWebStatus,
  echoWelcomeSeen: creator.echoWelcomeSeen,
  platforms: {
    instagram: {
      profilePicUrl: creator.platforms.instagram?.avatar ?? '',
      name: creator.platforms.instagram?.name ?? '',
      isFirstGraphAuth: !creator.platforms.instagram?.isConnected,
      isGraphTokenValid: !!creator.platforms.instagram?.isTokenValid,
      shouldGraphReAuth: !!creator.platforms.instagram?.shouldReAuth,
      graphAuthReminderSentAt: creator.platforms.instagram?.reminderSentAt,
      graphAuthReminderSentCount:
        creator.platforms.instagram?.reminderSentCount ?? 0,
      handle: creator.platforms.instagram?.handle ?? '',
      followersCount: creator.platforms.instagram?.followersCount ?? 0,
      followsCount: creator.platforms.instagram?.followersCount ?? 0,
      engagementRate: 0,
      mediaCount: 0,
    },
    tiktok: {
      name: creator.platforms.tiktok?.name ?? '',
      isTiktokConnected: !!creator.platforms.tiktok?.isConnected,
      isTiktokTokenValid: !!creator.platforms.tiktok?.isTokenValid,
      profileImage: creator.platforms.tiktok?.avatar ?? '',
      shouldTiktokReAuth: !!creator.platforms.tiktok?.shouldReAuth,
      tiktokAuthReminderSentCount:
        creator.platforms.tiktok?.reminderSentCount ?? 0,
      tiktokAuthReminderSentAt: creator.platforms.tiktok?.reminderSentAt,
      tiktokHandle: creator.platforms.tiktok?.handle ?? '',
    },
    youtube: {
      handle: creator.platforms.youtube?.handle ?? '',
      channelName: creator.platforms.youtube?.name ?? '',
      isYoutubeConnected: !!creator.platforms.youtube?.isConnected,
      isYoutubeTokenValid: !!creator.platforms.youtube?.isTokenValid,
      shouldYtReAuth: !!creator.platforms.youtube?.shouldReAuth,
      subscriberCount: creator.platforms.youtube?.followersCount ?? 0,
      ytAuthReminderSentCount:
        creator.platforms.youtube?.reminderSentCount ?? 0,
      followersCount: creator.platforms.youtube?.followersCount ?? 0,
      ytAuthReminderSentAt: creator.platforms.youtube?.reminderSentAt,
    },
  },
});

export const mapCreatorsFromApiModelToViewModel = (
  creators: clientApiCreatorModel.Creator[],
): viewModel.ICreator[] => creators.map(mapCreatorFromApiModelToViewModel);

export const mapNotificationsFromApiModelToViewModel = (
  notifications: clientApiNotificationModel.INotificationType[],
): notificationsViewModel.ISupportedNotificationType[] =>
  notifications.map(
    ({ read, ...notification }) =>
      ({
        ...notification,
        isRead: read,
        params: {
          ...notification.params,
          creator: mapCreatorFromApiModelToViewModel(
            notification.params.creator as clientApiCreatorModel.Creator,
          ),
        },
      }) as any,
  );
